<template>
  <v-card color="#121212" class="rounded-xl border-glow" flat>
    <div class="pa-5">
      <div class="d-flex align-center mb-4">
        <v-avatar color="red darken-3" size="40" class="mr-3 elevation-4">
          <v-icon small color="white">mdi-file-cancel</v-icon>
        </v-avatar>
        <div>
          <h3 class="text-h6 font-weight-bold white--text">Kündigungs-Assistent</h3>
          <div class="caption grey--text">AI hat "Kündigung" erkannt</div>
        </div>
        <v-spacer></v-spacer>
        <v-btn icon small color="grey" @click="resetWizard" v-if="step > 1">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </div>

      <v-divider class="grey darken-3 mb-4"></v-divider>

      <div v-if="step === 0">
        <p class="body-2 grey--text text--lighten-1 mb-3">Welchen Vertrag möchte {{ user.fullName }} kündigen?</p>

        <div v-if="loadingContracts" class="text-center py-4">
          <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
        </div>

        <div v-else-if="rawContracts.length === 0" class="text-center py-4 error--text">Keine aktiven Verträge im TAC gefunden.</div>

        <v-list v-else color="transparent" dark dense two-line>
          <v-list-item-group v-model="selectedContractIndex" color="primary">
            <v-list-item
              v-for="(contract, i) in activeRawContracts"
              :key="i"
              class="mb-2 rounded-lg"
              style="background: rgba(255, 255, 255, 0.05)"
              @click="selectContract(contract)">
              <v-list-item-avatar>
                <v-icon :color="getContractColor(contract)">mdi-card-account-details-outline</v-icon>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title class="font-weight-bold">{{ contract.NAME }}</v-list-item-title>
                <v-list-item-subtitle class="caption">
                  Bis: {{ formatDate(contract.TERM_ENDDATE) }} | {{ contract.mappedStudio?.name || "Studio" }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-icon small color="grey">mdi-chevron-right</v-icon>
              </v-list-item-action>
            </v-list-item>
          </v-list-item-group>
        </v-list>
      </div>

      <div v-else-if="step === 1">
        <div class="d-flex align-center mb-3">
          <v-btn icon small @click="step = 0" class="mr-2"><v-icon>mdi-arrow-left</v-icon></v-btn>
          <span class="font-weight-bold white--text">{{ activeContract.NAME }}</span>
        </div>

        <v-alert :color="isLate ? 'orange darken-4' : 'green darken-3'" dense text class="mb-4 rounded-lg">
          <div class="d-flex align-start">
            <v-icon :color="isLate ? 'orange' : 'green'" left>
              {{ isLate ? "mdi-clock-alert" : "mdi-check-circle" }}
            </v-icon>
            <div>
              <div class="font-weight-bold text-caption">{{ isLate ? "Verspätet / Automatische Verlängerung" : "Fristgerecht" }}</div>
              <div class="caption">
                Kündigung per: <strong>{{ formattedTerminationDate }}</strong>
              </div>
            </div>
          </div>
        </v-alert>

        <p class="caption grey--text mb-2">Kündigungsgrund wählen:</p>
        <v-select
          v-model="selectedReasonId"
          :items="reasons"
          item-text="Name"
          item-value="ReasonID"
          outlined
          dark
          dense
          rounded
          background-color="#1A1A1A"
          placeholder="Grund wählen..."
          :loading="loadingDetails"></v-select>

        <div v-if="isLate && !contractToTerminate" class="caption red--text mb-3">
          Fehler: Kein Folgevertrag für die verspätete Kündigung gefunden.
        </div>

        <v-btn block depressed color="primary" class="rounded-lg" :disabled="!selectedReasonId || !contractToTerminate" @click="step = 2">
          Weiter zur Prüfung
        </v-btn>
      </div>

      <div v-else-if="step === 2">
        <div class="text-center py-2">
          <p class="body-2 white--text mb-4">Möchtest du die Kündigung im TAC System jetzt <strong>verbindlich eintragen</strong>?</p>
          <div class="d-flex justify-space-between px-4">
            <v-btn text small color="grey" @click="step = 1">Zurück</v-btn>
            <v-btn color="red darken-1" depressed class="rounded-lg white--text" :loading="processing" @click="executeTermination">
              <v-icon left small>mdi-delete-forever</v-icon> Kündigen
            </v-btn>
          </div>
        </div>
      </div>

      <div v-else-if="step === 3">
        <v-alert type="success" dense text icon="mdi-check-circle" class="mb-4 rounded-lg"> Erfolgreich im TAC gekündigt! </v-alert>

        <p class="caption grey--text mb-1">Antwort an Kunden:</p>
        <v-card dark color="#000" class="pa-3 mb-3 rounded-lg border-subtle">
          <v-textarea
            v-model="mail.text"
            hide-details
            auto-grow
            rows="6"
            background-color="transparent"
            flat
            solo
            class="body-2 white--text"
            style="font-family: 'JetBrains Mono', monospace; line-height: 1.5"></v-textarea>
        </v-card>

        <div class="d-flex flex-wrap gap-2 mb-3">
          <v-chip dark color="grey darken-3" small label @click="previewPdf">
            <v-icon left x-small color="red">mdi-file-pdf-box</v-icon> PDF Vorschau
            <v-progress-circular v-if="pdfLoading" indeterminate size="12" width="2" class="ml-2"></v-progress-circular>
          </v-chip>
        </div>

        <div class="d-flex justify-end">
          <v-btn text small color="grey" class="mr-2" @click="$emit('cancel')">Abbrechen</v-btn>
          <v-btn color="success" depressed class="rounded-lg gradient-btn" :loading="sendingMail" @click="sendTerminationEmail">
            <v-icon left small>mdi-send</v-icon> Bestätigung Senden
          </v-btn>
        </div>
      </div>
    </div>
  </v-card>
</template>

<script>
import tacApi from "@/api/tacApi"
import api from "@/api"
import { format, addDays, addYears, isAfter } from "date-fns"
import { mapState } from "vuex"

export default {
  name: "MailTerminationWizard",
  props: {
    user: Object, // Das "customerData" Objekt aus der Mail
  },
  data: () => ({
    roodyUser: null,
    step: 0,
    loadingContracts: false,
    loadingDetails: false,
    processing: false,
    pdfLoading: false,
    sendingMail: false,

    rawContracts: [], // Die echten TAC Verträge
    selectedContractIndex: null,

    activeContract: null,
    contractToTerminate: null,

    reasons: [],
    selectedReasonId: null,

    isLate: false,
    terminationDate: null,

    mail: {
      to: "",
      subject: "Kündigungsbestätigung well come FIT",
      text: "",
    },
  }),

  computed: {
    ...mapState("studioStore", ["storeStudios"]),
    activeRawContracts() {
      // Filtere nur laufende oder zukünftige Verträge für die Anzeige
      if (!this.rawContracts) return []
      const today = new Date()
      today.setHours(0, 0, 0, 0)

      return this.rawContracts
        .filter((c) => {
          const end = new Date(c.TERM_ENDDATE)
          // return end >= today && c.CONTRACTTYP !== "Zusatzvertrag" // Optional: Nur Hauptverträge anzeigen?
          return end >= today // Optional: Nur Hauptverträge anzeigen?
        })
        .sort((a, b) => new Date(a.TERM_ENDDATE) - new Date(b.TERM_ENDDATE))
    },
    selectedReasonName() {
      const r = this.reasons.find((x) => x.ReasonID === this.selectedReasonId)
      return r ? r.Name : ""
    },
    formattedTerminationDate() {
      if (!this.terminationDate) return ""
      return format(this.terminationDate, "dd.MM.yyyy")
    },
  },

  mounted() {
    this.fetchContracts()
  },

  methods: {
    formatDate(d) {
      if (!d) return ""
      return new Date(d).toLocaleDateString()
    },

    getContractColor(c) {
      if (c.CONTRACTSTATE === 1) return "green"
      if (c.CONTRACTSTATE === 4) return "orange"
      return "grey"
    },

    resetWizard() {
      this.step = 0
      this.activeContract = null
      this.contractToTerminate = null
      this.selectedContractIndex = null
    },

    async fetchContracts() {
      this.loadingContracts = true
      try {
        // testing
        let mail = this.user.email
        //mail = "loepfezoe@gmail.com"
        let roodyUser = await api.getUserForManagerByEmail(mail)
        this.roodyUser = roodyUser
        let userId = roodyUser._id

        let res = await tacApi.fetchTACContractDetails(userId) // tacId muss ggf im Mapping hinzugefügt werden
        res = res.map((e) => {
          return {
            mappedStudio: this.storeStudios.find((s) => s.tacPropertyId && s.tacPropertyId.toString().trim() === e.RESSORTGROUPID.toString().trim()),
            ...e,
          }
        })
        this.rawContracts = res || []
      } catch (e) {
        console.error(e)
      } finally {
        this.loadingContracts = false
      }
    },

    async selectContract(contract) {
      this.activeContract = contract
      this.step = 1
      this.loadingDetails = true

      // 1. Berechnung (Late/OnTime)
      this.calculateTerminationDetails()

      // 2. Gründe laden
      try {
        const response = await tacApi.getTACReasons(contract.RESSORTGROUPID, "MEMBERSHIP_TERMINATION")
        //const response = await tacApi.getTACReasons(contract.RESSORTGROUPID, "CANCELLATION")
        if (response && response.Reasons && response.Reasons.Reason) {
          this.reasons = response.Reasons.Reason.map((r) => ({
            ReasonID: parseInt(r.ReasonID.UniqueID[0].value, 10),
            Name: r.Reasons.TextElement.find((t) => t.language === "de")?.value || r.Reasons.TextElement[0]?.value,
          })).filter((reason, index, self) => index === self.findIndex((t) => t.Name === reason.Name))
        }
      } catch (e) {
        console.error(e)
      } finally {
        this.loadingDetails = false
      }
    },

    calculateTerminationDetails() {
      // Deine Logik 1:1 übernommen
      const CANCELLATION_PERIOD_DAYS = 45
      const today = new Date()
      const currentTermEnd = new Date(this.activeContract.TERM_ENDDATE)
      const deadline = addDays(currentTermEnd, -CANCELLATION_PERIOD_DAYS)

      this.isLate = isAfter(today, deadline)

      if (this.isLate) {
        // Suche Folgevertrag
        const nextContract = this.rawContracts
          .filter((c) => c.CONTRACTTYP === "Hauptvertrag" && new Date(c.TERM_STARTDATE) > currentTermEnd)
          .sort((a, b) => new Date(a.TERM_STARTDATE) - new Date(b.TERM_STARTDATE))[0]

        if (nextContract) {
          this.contractToTerminate = nextContract
          this.terminationDate = new Date(nextContract.TERM_ENDDATE)
        } else {
          // Logik Fallback (simuliert)
          this.contractToTerminate = null
          let years = 1
          if (this.activeContract.NAME.includes("2 Jahr")) years = 2
          this.terminationDate = addYears(currentTermEnd, years)
        }
        this.mail.text = this.getLateEmailHtml()
      } else {
        this.terminationDate = currentTermEnd
        this.contractToTerminate = this.activeContract
        this.mail.text = this.getRegularEmailHtml()
      }
      this.mail.to = this.user.email
    },

    async executeTermination() {
      this.processing = true
      try {
        const data = {
          ResortId: this.contractToTerminate.RESSORTGROUPID,
          ContractId: this.contractToTerminate.CONTRACTID,
          ReasonId: this.selectedReasonId,
          ReasonType: "MEMBERSHIP_TERMINATION",
          CheckTermination: false,
        }

        const response = await tacApi.terminateTACContract(data)
        if (response?.Result?.resultStatusFlag === "SUCCESS") {
          this.step = 3
        } else {
          throw new Error(response?.Result?.Text?.[0]?.value || "TAC Error")
        }
      } catch (e) {
        this.$toast.error("Kündigung fehlgeschlagen: " + e.message)
      } finally {
        this.processing = false
      }
    },

    async sendTerminationEmail() {
      this.sendingMail = true
      try {
        // PDF Logic wie gehabt
        const blob = await this.getPdfBlob()
        if (!blob) throw new Error("PDF Error")

        const base64 = await this.blobToBase64(blob)
        const htmlBody = this.mail.text.replace(/\n/g, "<br>")

        const mailData = {
          to: this.mail.to,
          subject: this.mail.subject,
          html: htmlBody,
          attachments: [
            {
              filename: "Kündigungsbestätigung.pdf",
              content: base64.split(",")[1],
              encoding: "base64",
              contentType: "application/pdf",
            },
          ],
        }

        await api.sendTerminationMail(mailData) // Nutze speziellen API Endpoint oder normalen Send mit Attachment

        this.$toast.success("Bestätigung gesendet!")
        this.$emit("completed") // Parent sagen: "Fertig, du kannst archivieren"
      } catch (e) {
        this.$toast.error("Send Error: " + e.message)
      } finally {
        this.sendingMail = false
      }
    },

    // --- Helpers (PDF, Text, Blob) ---
    async getPdfBlob() {
      this.pdfLoading = true
      try {
        // Vereinfacht: Wir zeigen hier nur den aktuell gekündigten Vertrag
        return await api.generateTerminationPdf({
          user: this.roodyUser,
          contractsToDisplay: [this.activeContract],
          terminationDate: this.formattedTerminationDate,
          isLate: this.isLate,
        })
      } catch (e) {
        console.error(e)
        return null
      } finally {
        this.pdfLoading = false
      }
    },
    async previewPdf() {
      const blob = await this.getPdfBlob()
      if (blob) window.open(URL.createObjectURL(blob))
    },
    blobToBase64(blob) {
      return new Promise((resolve) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        reader.readAsDataURL(blob)
      })
    },
    getRegularEmailHtml() {
      return `Hallo ${
        this.user.fullName.replace("Herr ", "").replace("Frau ", "").split(" ")[0] || "Mitglied"
      }\n\nAnbei senden wir dir die Kündigungsbestätigung für deinen Vertrag '${
        this.activeContract.NAME
      }'.\n\nWir bedauern deinen Entscheid sehr. Wir sind stets daran, uns zu verbessern. Dürfen wir dich bitten, uns den Grund für Deine Kündigung kurz mitzuteilen?\n\nWir würden uns freuen, dich in Zukunft wieder bei uns begrüssen zu dürfen.\n\nSportliche Grüsse\nwell come FIT Team`
    },
    getLateEmailHtml() {
      // Dein neuer Text wird hier verwendet und formatiert
      return (
        `Hallo ${this.user.fullName.split(" ")[0] || "Mitglied"}\n\n` +
        `Gemäss unseren Mitgliedschaftsbedingungen verlängert sich die Mitgliedschaft automatisch, wenn diese nicht fristgerecht und schriftlich (spätestens 45 Tage vor Vertragsablauf) gekündigt wird (siehe Punkt 2 der Mitgliedschaftsbedingungen auf dem Vertrag).\n\n` +
        `Die Kündigungsfrist in deinem Fall ist bereits abgelaufen. Der Vertrag lässt sich nur per Vertragsablauf kündigen. Anbei senden wir dir die Kündigungsbestätigung per ${this.formattedTerminationDate}.\n\n` +
        `Bitte beachte, dass dein Mitgliedschaftsbeitrag somit auf Abonnementbeginn fällig ist.\n\n` +
        `Folgende Varianten können wir dir anbieten:\n\n` +
        `  •  Zahlung des Mitgliedschaftsbeitrages in Raten\n` +
        `     (2 oder 12 Raten - dafür benötigen wir einen neuen Vertrag, es wird ein Ratenzuschlag erhoben)\n\n` +
        `  •  Time-Stop\n` +
        `     (Unterbruch des Abonnements - gebührenpflichtig)\n` +
        `     die Zahlung des Mitgliedschaftsbeitrags bleibt bestehen / die Zahlungen der vereinbarten Raten bleibt bestehen\n\n` +
        `  •  Übergabe der Mitgliedschaft an ein Nichtmitglied (mit unterzeichneter, schriftlicher Bestätigung)\n` +
        `     (Umschreibegebühr und Aktivierungstarif total CHF 119.00)\n\n` +
        `Wir danken dir für deine Kenntnisnahme.\n\n` +
        `Sportliche Grüsse\n` +
        `well come FIT AG`
      )
    },
  },
}
</script>

<style scoped>
.border-glow {
  border: 1px solid rgba(255, 64, 64, 0.3);
  box-shadow: 0 0 15px rgba(255, 0, 0, 0.05);
}
.border-subtle {
  border: 1px solid rgba(255, 255, 255, 0.1);
}
.gap-2 {
  gap: 8px;
}
.gradient-btn {
  background: linear-gradient(135deg, #43a047 0%, #2e7d32 100%);
}
</style>
